import React, { useState, useContext } from 'react';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const SupportButton = () => {
  const { user, API, language } = useContext(AuthContext);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const content = {
    en: {
      support: 'Support',
      contactSupport: 'Contact Support',
      description: 'Need help? Submit a support ticket and our team will get back to you shortly.',
      email: 'Email',
      title: 'Title',
      titlePlaceholder: 'Brief title for your issue',
      subject: 'Subject',
      subjectPlaceholder: 'Subject of your request',
      message: 'Message',
      messagePlaceholder: 'Describe your issue in detail...',
      cancel: 'Cancel',
      submit: 'Submit Ticket',
      success: 'Support ticket submitted successfully! We\'ll get back to you soon.',
      error: 'Failed to submit support ticket. Please try again.',
      fillAllFields: 'Please fill in all fields'
    },
    de: {
      support: 'Support',
      contactSupport: 'Support kontaktieren',
      description: 'Benötigen Sie Hilfe? Senden Sie ein Support-Ticket und unser Team meldet sich in Kürze bei Ihnen.',
      email: 'E-Mail',
      title: 'Titel',
      titlePlaceholder: 'Kurzer Titel für Ihr Anliegen',
      subject: 'Betreff',
      subjectPlaceholder: 'Betreff Ihrer Anfrage',
      message: 'Nachricht',
      messagePlaceholder: 'Beschreiben Sie Ihr Anliegen im Detail...',
      cancel: 'Abbrechen',
      submit: 'Ticket senden',
      success: 'Support-Ticket erfolgreich eingereicht! Wir melden uns bald bei Ihnen.',
      error: 'Support-Ticket konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
      fillAllFields: 'Bitte füllen Sie alle Felder aus'
    },
    it: {
      support: 'Supporto',
      contactSupport: 'Contatta il Supporto',
      description: 'Hai bisogno di aiuto? Invia un ticket di supporto e il nostro team ti risponderà a breve.',
      email: 'Email',
      title: 'Titolo',
      titlePlaceholder: 'Titolo breve per il tuo problema',
      subject: 'Oggetto',
      subjectPlaceholder: 'Oggetto della tua richiesta',
      message: 'Messaggio',
      messagePlaceholder: 'Descrivi il tuo problema in dettaglio...',
      cancel: 'Annulla',
      submit: 'Invia Ticket',
      success: 'Ticket di supporto inviato con successo! Ti risponderemo presto.',
      error: 'Impossibile inviare il ticket di supporto. Riprova.',
      fillAllFields: 'Per favore compila tutti i campi'
    },
    fr: {
      support: 'Support',
      contactSupport: 'Contacter le Support',
      description: 'Besoin d\'aide? Soumettez un ticket de support et notre équipe vous répondra sous peu.',
      email: 'Email',
      title: 'Titre',
      titlePlaceholder: 'Titre bref pour votre problème',
      subject: 'Sujet',
      subjectPlaceholder: 'Sujet de votre demande',
      message: 'Message',
      messagePlaceholder: 'Décrivez votre problème en détail...',
      cancel: 'Annuler',
      submit: 'Soumettre le Ticket',
      success: 'Ticket de support soumis avec succès! Nous vous répondrons bientôt.',
      error: 'Impossible de soumettre le ticket de support. Veuillez réessayer.',
      fillAllFields: 'Veuillez remplir tous les champs'
    }
  };

  const t = content[language] || content.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.subject || !formData.message) {
      toast.error(t.fillAllFields);
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/support/tickets`, formData);
      toast.success(t.success);
      setDialogOpen(false);
      setFormData({ title: '', subject: '', message: '' });
    } catch (error) {
      toast.error(t.error);
      console.error('Support ticket error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setDialogOpen(true)}
        className="flex items-center gap-2"
        data-testid="support-btn"
      >
        <HelpCircle className="w-4 h-4" />
        {t.support}
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px] bg-white">
          <DialogHeader>
            <DialogTitle>{t.contactSupport}</DialogTitle>
            <DialogDescription>{t.description}</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>{t.email}</Label>
              <Input
                type="email"
                value={user?.email || ''}
                disabled
                className="bg-gray-50"
              />
            </div>

            <div>
              <Label>{t.title}</Label>
              <Input
                placeholder={t.titlePlaceholder}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div>
              <Label>{t.subject}</Label>
              <Input
                placeholder={t.subjectPlaceholder}
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                required
              />
            </div>

            <div>
              <Label>{t.message}</Label>
              <Textarea
                placeholder={t.messagePlaceholder}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={5}
                required
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={submitting}
              >
                {t.cancel}
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (language === 'en' ? 'Submitting...' : language === 'it' ? 'Invio...' : language === 'fr' ? 'Envoi...' : 'Wird gesendet...') : t.submit}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SupportButton;
